import { useEffect, useState, useCallback } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Skeleton,
    Stack,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListItemText,
    ListItemAvatar,
    ListItem,
    List,
    AccordionDetails,
    AccordionSummary,
    Accordion
} from '@mui/material';

import {
    AreaChart,
    Area,
    ResponsiveContainer,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import { motion, AnimatePresence } from 'framer-motion';

import {
    FolderOpen,
    FileText,
    Euro,
    Plus,
    CreditCard,
    Clock,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Receipt,
    Wallet,
} from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { toast } from 'sonner';
import { useAuthStore } from '../../../store/useAuthStore';
import apiClient from '../../../services/api-client';
import { CreateDossierPage } from '../../orders/pages/CreateDossierPage';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { AddPaymentDialog } from '../../paiement/components/AddPaymentDialog';

// ============================================================
// THEME TOKENS
// ============================================================

const COLORS = {
    gold: '#c9a84c',
    goldDark: '#b28f34',
    navy: '#1a1a2e',
    navyLight: '#2a2a4e',
    bg: '#faf7f2',
    textSecondary: '#666',
    textMuted: '#999',
    border: 'rgba(201,168,76,0.15)',
};

// ============================================================
// STATUS CONFIG
// ============================================================

const DOSSIER_STATUS_CONFIG = {
    BROUILLON: {
        label: 'Brouillon',
        color: '#9e9e9e',
        bgColor: 'rgba(158,158,158,.12)',
        icon: <FolderOpen size={16} />,
    },
    CONFIRME: {
        label: 'Confirmé',
        color: '#2e7d32',
        bgColor: 'rgba(46,125,50,.12)',
        icon: <CheckCircle2 size={16} />,
    },
    TERMINE: {
        label: 'Terminé',
        color: COLORS.gold,
        bgColor: 'rgba(201,168,76,.12)',
        icon: <CheckCircle2 size={16} />,
    },
    ANNULÉ: {
        label: 'Annulé',
        color: '#c62828',
        bgColor: 'rgba(198,40,40,.12)',
        icon: <XCircle size={16} />,
    },
};

const PAYMENT_METHOD_LABELS = {
    VIREMENT: {
        label: 'Virement',
        icon: <Wallet size={14} />,
    },
    CARTE: {
        label: 'Carte bancaire',
        icon: <CreditCard size={14} />,
    },
    ESPECES: {
        label: 'Espèces',
        icon: <Euro size={14} />,
    },
    CHEQUE: {
        label: 'Chèque',
        icon: <Receipt size={14} />,
    },
};

// ============================================================
// HELPERS
// ============================================================

function formatCurrency(value) {
    return (
        value.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
}

function formatNumber(value) {
    return value.toLocaleString('fr-FR');
}

function getRelativeTime(timestamp) {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} j`;

    return new Date(timestamp).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
    });
}

// ============================================================
// MUI CARD STYLE
// ============================================================

const luxuryCardSx = {
    borderRadius: 0.5,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.bg,
    boxShadow: '0 4px 20px rgba(0,0,0,.04)',
};

// ============================================================
// KPI CARD
// ============================================================

function KPICard({
    title,
    value,
    subtitle,
    icon,
    color,
    bgColor,
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <Card
                sx={{
                    ...luxuryCardSx,
                    transition: '.25s',
                    '&:hover': {
                        boxShadow: '0 10px 28px rgba(0,0,0,.08)',
                    },
                }}
            >
                <CardContent>
                    <Stack spacing={2}>
                        <Avatar
                            sx={{
                                bgcolor: bgColor,
                                color,
                                width: 44,
                                height: 44,
                            }}
                        >
                            {icon}
                        </Avatar>

                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: COLORS.textSecondary,
                                    mb: .5,
                                }}
                            >
                                {title}
                            </Typography>

                            <Typography
                                variant="h5"
                                fontWeight={700}
                                sx={{
                                    color: COLORS.navy,
                                    fontFamily:
                                        "'Cormorant Garamond', serif",
                                }}
                            >
                                {value}
                            </Typography>

                            {subtitle && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: COLORS.textMuted,
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </motion.div>
    );
}

// ============================================================
// CHART CARD
// ============================================================

function ChartCard({
    title,
    subtitle,
    children,
}) {
    return (
        <Card sx={luxuryCardSx}>
            <CardHeader
                title={
                    <Typography
                        variant="h5"
                        sx={{
                            color: COLORS.navy,
                            fontWeight: 700,
                        }}
                    >
                        {title}
                    </Typography>
                }
                subheader={
                    subtitle && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: COLORS.textMuted,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )
                }
                sx={{ pb: 1 }}
            />

            <CardContent>{children}</CardContent>
        </Card>
    );
}

// ============================================================
// RECHART TOOLTIP
// ============================================================

function CustomTooltip({
    active,
    payload,
    label,
    formatter,
}) {
    if (!active || !payload?.length) return null;

    return (
        <Card
            sx={{
                p: 1.5,
                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                bgcolor: COLORS.bg,
                minWidth: 150,
            }}
        >
            <Typography
                variant="subtitle2"
                sx={{
                    color: COLORS.navy,
                    fontWeight: 600,
                    mb: .5,
                }}
            >
                {label}
            </Typography>

            {payload.map((entry, i) => (
                <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: entry.color,
                        }}
                    />

                    <Typography
                        variant="caption"
                        sx={{ color: COLORS.textSecondary }}
                    >
                        {entry.name}:{' '}
                        {formatter
                            ? formatter(entry.value)
                            : formatNumber(entry.value)}
                    </Typography>
                </Stack>
            ))}
        </Card>
    );
}

// ============================================================
// LOADING SKELETON
// ============================================================

function LoadingSkeleton() {
    return (
        <Stack spacing={3}>
            <Grid container spacing={2}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={2.4}
                        key={i}
                    >
                        <Skeleton
                            variant="rounded"
                            height={120}
                            sx={{ borderRadius: 4 }}
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Skeleton
                        variant="rounded"
                        height={300}
                        sx={{ borderRadius: 4 }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Skeleton
                        variant="rounded"
                        height={300}
                        sx={{ borderRadius: 4 }}
                    />
                </Grid>
            </Grid>
        </Stack>
    );
}

// ============================================================
// ERROR STATE
// ============================================================

function ErrorState({
    message,
    onRetry,
}) {
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            gap={2}
        >
            <Alert
                severity="error"
                icon={<AlertTriangle size={18} />}
                sx={{
                    width: '100%',
                    maxWidth: 500,
                    borderRadius: 3,
                }}
            >
                {message}
            </Alert>

            <Button
                variant="outlined"
                startIcon={<RefreshCw size={15} />}
                onClick={onRetry}
                sx={{
                    borderColor: COLORS.gold,
                    color: COLORS.gold,
                    borderRadius: 3,
                    '&:hover': {
                        borderColor: COLORS.goldDark,
                        bgcolor: 'rgba(201,168,76,.05)',
                    },
                }}
            >
                Réessayer
            </Button>
        </Box>
    );
}

// ============================================================
// DOSSIERS LIST
// ============================================================

function DossierSection({
    dossierDetails,
    expandedDossier,
    setExpandedDossier,
}) {
    return (
        <ChartCard
            title="Détail des dossiers"
            subtitle={`${dossierDetails.length} dossier(s)`}
        >
            <Stack spacing={1}>
                {dossierDetails.map((dossier) => {
                    const status =
                        DOSSIER_STATUS_CONFIG[dossier.status] ||
                        DOSSIER_STATUS_CONFIG.BROUILLON;

                    const isExpanded =
                        expandedDossier === dossier.id;

                    const progress =
                        dossier.totalInvoiced > 0
                            ? (dossier.totalPaid /
                                dossier.totalInvoiced) *
                            100
                            : 0;

                    return (
                        <Accordion
                            key={dossier.id}
                            expanded={isExpanded}
                            onChange={() =>
                                setExpandedDossier(
                                    isExpanded
                                        ? null
                                        : dossier.id
                                )
                            }
                            sx={{
                                borderRadius: 3,
                                border: `1px solid ${COLORS.border}`,
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                            >
                                <Box
                                    sx={{
                                        display: "flex", flexDirection: "row",
                                        gap: 2, alignItems: "center", width: "100%", justifyContent: "space-between"
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
                                        <Box
                                            sx={{
                                                color: status.color,
                                                bgcolor:
                                                    status.bgColor,
                                                p: 1,
                                                borderRadius: 2,
                                            }}
                                        >
                                            {status.icon}
                                        </Box>

                                        <Box flex={1}>
                                            <Typography fontWeight={600}>
                                                {dossier.title}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {dossier.reference}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Chip
                                        label={
                                            status.label
                                        }
                                        size="small"
                                        sx={{
                                            bgcolor:
                                                status.bgColor,
                                            color: status.color,
                                        }}
                                    />

                                    <Typography
                                        fontWeight={600}
                                    >
                                        {formatCurrency(
                                            dossier.totalInvoiced
                                        )}
                                    </Typography>
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Stack spacing={2}>
                                    <Divider />

                                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                                        <Box sx={{ width: "100%" }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Facturé
                                            </Typography>
                                            <Typography>
                                                {formatCurrency(
                                                    dossier.totalInvoiced
                                                )}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ width: "100%" }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Payé
                                            </Typography>
                                            <Typography
                                                color="success.main"
                                            >
                                                {formatCurrency(
                                                    dossier.totalPaid
                                                )}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ width: "100%" }}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Reste
                                            </Typography>
                                            <Typography
                                                color="warning.main"
                                            >
                                                {formatCurrency(
                                                    dossier.remaining
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{
                                                height: 8,
                                                borderRadius: 5,
                                                bgcolor:
                                                    'rgba(0,0,0,.05)',
                                                '& .MuiLinearProgress-bar':
                                                {
                                                    bgcolor:
                                                        COLORS.gold,
                                                },
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                mt: 1,
                                                display:
                                                    'block',
                                            }}
                                        >
                                            {progress.toFixed(
                                                0
                                            )}
                                            % payé
                                        </Typography>
                                    </Box>
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </ChartCard>
    );
}

// ============================================================
// PAYMENTS LIST
// ============================================================

function PaymentSection({ payments }) {
    return (
        <ChartCard
            title="Paiements"
            subtitle={`${payments.length} paiement(s)`}
        >
            <List>
                {payments.map((p) => (
                    <ListItem
                        key={p.id}
                        divider
                        secondaryAction={
                            <Typography fontWeight={600}>
                                {formatCurrency(p.amount)}
                            </Typography>
                        }
                    >
                        <ListItemAvatar>
                            <Avatar
                                sx={{
                                    bgcolor:
                                        'rgba(46,125,50,.1)',
                                    color: '#2e7d32',
                                }}
                            >
                                <CreditCard size={16} />
                            </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                            primary={p.invoiceNumber}
                            secondary={`${p.clientName} • ${p.paymentMethod}`}
                        />
                    </ListItem>
                ))}
            </List>
        </ChartCard>
    );
}

// ============================================================
// NEW DOSSIER DIALOG
// ============================================================

function NewDossierDialog({
    open,
    onClose,
    newDossier,
    setNewDossier,
    onSubmit,
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogContent>
                <CreateDossierPage onClose={onClose} />
            </DialogContent>
        </Dialog>
    );
}

// ============================================================
// NEW PAYMENT DIALOG
// ============================================================

function NewPaymentDialog({
    open,
    onClose,
    newPayment,
    setNewPayment,
    availableInvoices,
    onSubmit,
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                Nouveau paiement
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <FormControl fullWidth>
                        <InputLabel>Facture</InputLabel>
                        <Select
                            value={newPayment.invoiceId}
                            onChange={(e) =>
                                setNewPayment({
                                    ...newPayment,
                                    invoiceId:
                                        e.target.value,
                                })
                            }
                        >
                            {availableInvoices.map(
                                (inv) => (
                                    <MenuItem
                                        key={inv.id}
                                        value={inv.id}
                                    >
                                        {inv.invoiceNumber}{' '}
                                        - {inv.clientName}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Montant"
                        type="number"
                        fullWidth
                        value={newPayment.amount}
                        onChange={(e) =>
                            setNewPayment({
                                ...newPayment,
                                amount: e.target.value,
                            })
                        }
                    />

                    <FormControl fullWidth>
                        <InputLabel>
                            Méthode
                        </InputLabel>
                        <Select
                            value={
                                newPayment.paymentMethod
                            }
                            onChange={(e) =>
                                setNewPayment({
                                    ...newPayment,
                                    paymentMethod:
                                        e.target.value,
                                })
                            }
                        >
                            {Object.entries(
                                PAYMENT_METHOD_LABELS
                            ).map(([key, v]) => (
                                <MenuItem
                                    key={key}
                                    value={key}
                                >
                                    {v.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Référence"
                        fullWidth
                        value={newPayment.reference}
                        onChange={(e) =>
                            setNewPayment({
                                ...newPayment,
                                reference:
                                    e.target.value,
                            })
                        }
                    />

                    <TextField
                        label="Notes"
                        fullWidth
                        value={newPayment.notes}
                        onChange={(e) =>
                            setNewPayment({
                                ...newPayment,
                                notes: e.target.value,
                            })
                        }
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Annuler
                </Button>

                <Button
                    variant="contained"
                    onClick={onSubmit}
                    sx={{
                        bgcolor: COLORS.gold,
                        color: COLORS.navy,
                    }}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AgentDashboard() {
    const user = useAuthStore((s) => s.user);

    const [expandedDossier, setExpandedDossier] = useState(null);

    const [newDossier, setNewDossier] = useState({});
    const [newPayment, setNewPayment] = useState({});
    const [openDossier, setOpenDossier] = useState(false);
    const [openPayment, setOpenPayment] = useState(false);
    const [availableInvoices, setAvailableInvoices] = useState([]);
    const [openGlobalPayment, setOpenGlobalPayment] = useState(false);

    const agentName = user
        ? `${user.firstName} ${user.lastName}`
        : '';

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['agent-dashboard'], // Clé unique pour tout le dashboard
        queryFn: async () => {
            const res = await apiClient.get('/api/reports/agent-dashboard');
            return res.data;
        },
        staleTime: 0, // Force le re-fetch immédiat si invalidé
    });

    if (isLoading) return <LoadingSkeleton />;
    if (isError)
        return (
            <ErrorState
                message="Impossible de charger les données"
                onRetry={refetch}
            />
        );

    if (!data) return null;

    const {
        dossierIndicators,
        invoiceIndicators,
        revenueEvolution,
        payments,
        dossierDetails,
    } = data;

    const handleNewOpen = () => {
        setOpenDossier(true);
    };

    const handleNewPaymentOpen = () => {
        setOpenPayment(true);
    };

    const handleCloseDossier = () => {
        setOpenDossier(false);
    };

    const handleClosePayment = () => {
        setOpenPayment(false);
    };

    return (
        <Box display="flex" flexDirection="column" gap={3}>
            {/* =========================================================
                HEADER
            ========================================================= */}
            <Card sx={{ ...luxuryCardSx, bgcolor: COLORS.navy, mb: 2 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex", flexDirection: "row", gap: 2,
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", md: "center" }
                        }}
                    >
                        <Box
                            sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}
                        >
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: '#fff',
                                        fontFamily:
                                            "'Cormorant Garamond', serif",
                                        fontWeight: 700,
                                    }}
                                >
                                    Bonjour, {user?.firstName}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255,255,255,.6)',
                                    }}
                                >
                                    Tableau de bord agent
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={<Plus size={16} />}
                                sx={{
                                    bgcolor: COLORS.gold,
                                    color: COLORS.navy,
                                    borderRadius: 0.5,
                                    '&:hover': {
                                        bgcolor: COLORS.goldDark,
                                    },
                                }}
                                onClick={handleNewOpen}
                            >
                                Nouveau dossier
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<CreditCard size={16} />}
                                onClick={() => setOpenGlobalPayment(true)} // Déclenchement ici
                                sx={{ borderColor: COLORS.gold, color: COLORS.gold }}
                            >
                                Paiement
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* =========================================================
                DOSSIER KPIs
            ========================================================= */}
            <Box>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        color: COLORS.navy,
                        fontWeight: 700,
                    }}
                >
                    Mes dossiers
                </Typography>

                <Box sx={{ width: '100%', display: "flex", flexWrap: "wrap", mb: 2, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <KPICard
                            title="Total"
                            value={dossierIndicators.total}
                            icon={<FolderOpen size={18} />}
                            color={COLORS.navy}
                            bgColor="rgba(26,26,46,.1)"
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <KPICard
                            title="Brouillon"
                            value={dossierIndicators.brouillon}
                            icon={<FolderOpen size={18} />}
                            color="#9e9e9e"
                            bgColor="rgba(158,158,158,.1)"
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <KPICard
                            title="Confirmés"
                            value={dossierIndicators.confirme}
                            icon={<CheckCircle2 size={18} />}
                            color="#2e7d32"
                            bgColor="rgba(46,125,50,.1)"
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <KPICard
                            title="Terminés"
                            value={dossierIndicators.termine}
                            icon={<CheckCircle2 size={18} />}
                            color={COLORS.gold}
                            bgColor="rgba(201,168,76,.1)"
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <KPICard
                            title="Annulés"
                            value={dossierIndicators.annule}
                            icon={<XCircle size={18} />}
                            color="#c62828"
                            bgColor="rgba(198,40,40,.1)"
                        />
                    </Box>
                </Box>

                {/* =========================================================
                FACTURES + CHIFFRE D’AFFAIRES + CHART
            ========================================================= */}
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, flexWrap: "wrap" }}>
                    {/* FACTURES */}
                    <Box sx={{ flex: 1 }}>
                        <ChartCard
                            title="Mes factures"
                            subtitle="Indicateurs globaux"
                        >
                            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, flexWrap: "wrap" }}>
                                <Box sx={{ flex: 1 }}>
                                    <KPICard
                                        title="En attente"
                                        value={invoiceIndicators.enAttente}
                                        icon={<Clock size={18} />}
                                        color={COLORS.gold}
                                        bgColor="rgba(201,168,76,.1)"
                                    />
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <KPICard
                                        title="Partiellement payées"
                                        value={
                                            invoiceIndicators.partiellementPayee
                                        }
                                        icon={<Receipt size={18} />}
                                        color="#ef6c00"
                                        bgColor="rgba(239,108,0,.1)"
                                    />
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <KPICard
                                        title="Payées"
                                        value={invoiceIndicators.payee}
                                        icon={<CheckCircle2 size={18} />}
                                        color="#2e7d32"
                                        bgColor="rgba(46,125,50,.1)"
                                    />
                                </Box>

                                <Divider />

                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: COLORS.textSecondary }}
                                    >
                                        Chiffre d’affaires
                                    </Typography>

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontFamily:
                                                "'Cormorant Garamond', serif",
                                            fontWeight: 700,
                                            color: COLORS.navy,
                                        }}
                                    >
                                        {formatCurrency(
                                            invoiceIndicators.chiffreAffaires
                                        )}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: COLORS.textMuted,
                                        }}
                                    >
                                        {invoiceIndicators.total} factures
                                    </Typography>
                                </Box>
                            </Box>
                        </ChartCard>
                    </Box>
                </Box>

                {/* CHART */}
                <Box sx={{ flex: 1 }} mt={3}>
                    <ChartCard
                        title="Évolution du chiffre d’affaires"
                        subtitle="Année en cours"
                    >
                        <Box height={320}>
                            <ResponsiveContainer
                                width="100%"
                                height="100%"
                            >
                                <AreaChart
                                    data={revenueEvolution}
                                >
                                    <defs>
                                        <linearGradient
                                            id="rev"
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor={COLORS.gold}
                                                stopOpacity={0.4}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor={COLORS.gold}
                                                stopOpacity={0.05}
                                            />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="rgba(0,0,0,.05)"
                                    />

                                    <XAxis
                                        dataKey="label"
                                        tick={{ fontSize: 11 }}
                                    />

                                    <YAxis
                                        tick={{ fontSize: 11 }}
                                    />

                                    <Tooltip
                                        content={
                                            <CustomTooltip
                                                formatter={
                                                    formatCurrency
                                                }
                                            />
                                        }
                                    />

                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke={COLORS.gold}
                                        fill="url(#rev)"
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartCard>
                </Box>

                {/* =========================================================
                PLACEHOLDER NEXT SECTIONS
                (Dossiers + Payments → Bloc 3)
            ========================================================= */}

                <Box mt={3}>
                    <DossierSection
                        dossierDetails={data.dossierDetails}
                        expandedDossier={expandedDossier}
                        setExpandedDossier={setExpandedDossier}
                    />
                </Box>
                <Box mt={3}>
                    <PaymentSection
                        payments={data.payments}
                    />
                </Box>
            </Box>
            {/* dialogs */}
            <NewDossierDialog
                open={openDossier}
                onClose={() => setOpenDossier(false)}
                newDossier={newDossier}
                setNewDossier={setNewDossier}
                onSubmit={() => { }}
            />
            <AddPaymentDialog
                open={openGlobalPayment}
                onOpenChange={setOpenGlobalPayment}
                onSuccess={() => {
                    // Rafraîchit les données financières du Dashboard en tâche de fond
                    QueryClient.invalidateQueries(['agent-dashboard']);
                }}
            />
        </Box>
    );
}

export default AgentDashboard;